import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Loader2, CheckCircle2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

// Pretty labels for horse info fields (capitalise DOB properly)
const FIELD_LABELS = {
  sale: 'Sale', consignor: 'Consignor', hip: 'Hip #', name: 'Name',
  sire: 'Sire', dam: 'Dam', dob: 'DOB', sex: 'Sex', color: 'Color',
};

// Text questions that are optional (not required)
const OPTIONAL_TEXT_IDS = ['special_instructions', 'other_info'];

// Questions that start with "If a colt" — allow N/A + auto-fill based on sex
const COLT_QUESTION_IDS = ['ridgling', 'gelded'];

const isColt = (sex) => {
  const s = (sex || '').toLowerCase();
  return s === 'male' || s === 'colt';
};

const YesNoQuestion = ({ question, answers, onChange, allowNA, readOnly }) => {
  const val = answers[question.id] || '';
  const followVal = answers[`${question.id}_followup`] || '';
  const isNA = val === 'na';

  return (
    <div className="space-y-1 py-2 border-b last:border-0">
      <p className={`text-sm ${isNA ? 'text-muted-foreground' : ''}`}>
        {question.text}<span className="ml-1 text-red-500">*</span>
      </p>
      <div className="flex gap-6 pl-2">
        {['yes', 'no', ...(allowNA ? ['na'] : [])].map(opt => (
          <label key={opt} className="flex items-center gap-1.5 cursor-pointer text-sm">
            <input
              type="radio"
              name={question.id}
              value={opt}
              checked={val === opt}
              onChange={() => !readOnly && onChange(question.id, opt)}
              className="accent-primary"
              disabled={readOnly || (isNA && opt !== 'na')}
              readOnly={readOnly}
            />
            {opt === 'yes' ? 'Yes' : opt === 'no' ? 'No' : 'N/A'}
          </label>
        ))}
      </div>
      {question.follow_up && val === 'yes' && (
        <div className="pl-2 pt-1">
          <Label className="text-xs text-muted-foreground">
            {question.follow_up}<span className="ml-1 text-red-500">*</span>
          </Label>
          <Textarea
            value={followVal}
            onChange={e => !readOnly && onChange(`${question.id}_followup`, e.target.value)}
            rows={2}
            className="mt-1 text-sm resize-none"
            placeholder="Required — please provide details..."
            disabled={readOnly}
          />
        </div>
      )}
    </div>
  );
};

const QuestionnaireCompletionDialog = ({ open, onClose, deliverableId, onCompleted }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formDef, setFormDef] = useState(null);
  const [answers, setAnswers] = useState({});
  const [signatory, setSignatory] = useState('');
  const [title, setTitle] = useState('Owner');
  const [otherTitle, setOtherTitle] = useState('');
  const [signDate, setSignDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (open && deliverableId) loadForm();
  }, [open, deliverableId]);

  const loadForm = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/questionnaires/form/${deliverableId}`);
      const data = res.data;
      setFormDef(data);

      const existingAnswers = data.existing_answers || {};

      // Auto-mark "If a colt" questions as N/A if horse is not a colt
      if (!isColt(data.horse_info?.sex)) {
        COLT_QUESTION_IDS.forEach(id => {
          if (!existingAnswers[id]) {
            existingAnswers[id] = 'na';
          }
        });
      }

      setAnswers(existingAnswers);
    } catch {
      toast.error('Failed to load questionnaire');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (id, val) => setAnswers(prev => ({ ...prev, [id]: val }));

  const handleSubmit = async () => {
    if (!signatory.trim()) { toast.error('Please enter your name to sign'); return; }
    if (title === 'Other' && !otherTitle.trim()) { toast.error('Please specify your title'); return; }

    // Check all yes/no questions have an answer
    const unanswered = (formDef?.yes_no_questions || []).filter(q => !answers[q.id]);
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions. ${unanswered.length} unanswered.`);
      return;
    }

    // Check required follow-up fields (triggered when answer is "yes" and follow_up exists)
    const unansweredFollowUps = (formDef?.yes_no_questions || []).filter(
      q => q.follow_up && answers[q.id] === 'yes' && !answers[`${q.id}_followup`]?.trim()
    );
    if (unansweredFollowUps.length > 0) {
      toast.error(`Please complete all required follow-up details. ${unansweredFollowUps.length} field(s) missing.`);
      return;
    }

    // Check required text questions are filled
    const unansweredText = (formDef?.text_questions || []).filter(
      q => !OPTIONAL_TEXT_IDS.includes(q.id) && !answers[q.id]?.trim()
    );
    if (unansweredText.length > 0) {
      toast.error(`Please complete all required fields. ${unansweredText.length} required text question(s) unanswered.`);
      return;
    }

    setSaving(true);
    try {
      const effectiveTitle = title === 'Other' ? otherTitle : title;
      const res = await api.post(`/questionnaires/complete/${deliverableId}`, {
        answers,
        signatory,
        title: effectiveTitle,
        date: new Date(signDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      });
      toast.success('Questionnaire completed and saved');
      onCompleted?.(res.data);
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to complete questionnaire');
    } finally {
      setSaving(false);
    }
  };

  const downloadCompleted = () => {
    if (!formDef?.completed_file_id) return;
    const token = localStorage.getItem('token');
    fetch(`${window.location.origin}/api/owner-portal/file-download/${formDef.completed_file_id}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.blob()).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = formDef.completed_filename || 'questionnaire-completed.pdf';
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const formType = formDef?.form_type || '';
  const isYearlingOrRacing = formType === 'questionnaire_yearling' || formType === 'questionnaire_racing_prospect';
  const horseIsColt = isColt(formDef?.horse_info?.sex);

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-2xl flex flex-col max-h-[90vh] p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {formDef?.form_title || 'Questionnaire'}
            {formDef?.is_completed && <Badge className="bg-green-600 text-white ml-2">Completed</Badge>}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : formDef ? (
          <div className="space-y-4">
            {/* Horse info banner with proper field labels */}
            <div className="bg-muted/30 rounded p-3 text-xs grid grid-cols-3 gap-1">
              {Object.entries(formDef.horse_info).filter(([, v]) => v).map(([k, v]) => (
                <div key={k}>
                  <span className="font-semibold">{FIELD_LABELS[k] || k}: </span>{v}
                </div>
              ))}
            </div>

            {/* Already completed */}
            {formDef.is_completed && (
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded">
                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800">Questionnaire completed</p>
                  {formDef.signatory && (
                    <p className="text-xs text-green-700">
                      Signed by <strong>{formDef.signatory}</strong>
                      {formDef.signatory_title ? ` (${formDef.signatory_title})` : ''}
                      {formDef.signed_date ? ` on ${formDef.signed_date}` : ''}
                    </p>
                  )}
                </div>
                {formDef.completed_file_id && (
                  <div>{/* download available via Document Vault */}</div>
                )}
              </div>
            )}

            <p className="text-xs text-muted-foreground italic">
              It is important to answer all questions truthfully and to the best of your knowledge.
            </p>

            {/* Yes/No Questions */}
            <div className="border rounded divide-y">
              {formDef.yes_no_questions.map(q => {
                const isColQuestion = COLT_QUESTION_IDS.includes(q.id);
                const allowNA = isColQuestion && isYearlingOrRacing;
                return (
                  <div key={q.id} className="px-3">
                    <YesNoQuestion
                      question={q}
                      answers={answers}
                      onChange={handleAnswer}
                      allowNA={allowNA}
                      readOnly={formDef.is_completed}
                    />
                  </div>
                );
              })}
            </div>

            {/* Text Questions */}
            {formDef.text_questions.map(q => {
              const isOptional = OPTIONAL_TEXT_IDS.includes(q.id);
              return (
                <div key={q.id} className="space-y-1">
                  <Label className="text-sm">
                    {q.text}
                    {isOptional
                      ? <span className="ml-1 text-xs text-muted-foreground font-normal">(optional)</span>
                      : <span className="ml-1 text-red-500">*</span>
                    }
                  </Label>
                  <Textarea
                    value={answers[q.id] || ''}
                    onChange={e => handleAnswer(q.id, e.target.value)}
                    rows={3}
                    className="resize-none text-sm"
                    placeholder={isOptional ? 'Optional — enter your response if applicable...' : 'Required — enter your response...'}
                    disabled={formDef.is_completed}
                  />
                </div>
              );
            })}

            {/* Signature Block */}
            {!formDef.is_completed && (
              <div className="border rounded p-4 space-y-3 bg-muted/10">
                <p className="text-sm font-semibold">Signature</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Title</Label>
                    <select
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      className="w-full h-9 text-sm border border-input rounded-md px-3 bg-background"
                    >
                      {formDef.title_options.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Date *</Label>
                    <Input type="date" value={signDate} onChange={e => setSignDate(e.target.value)} className="h-9 text-sm" />
                  </div>
                </div>
                {/* Other title text box */}
                {title === 'Other' && (
                  <div className="space-y-1">
                    <Label className="text-xs">Please specify title *</Label>
                    <Input
                      value={otherTitle}
                      onChange={e => setOtherTitle(e.target.value)}
                      placeholder="e.g. Veterinarian, Bloodstock Agent..."
                      className="text-sm"
                    />
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-xs">Your Full Name (Digital Signature) *</Label>
                  <Input
                    value={signatory}
                    onChange={e => setSignatory(e.target.value)}
                    placeholder="Type your full name to sign"
                    className="text-sm"
                  />
                  {signatory && (
                    <p className="text-xs text-muted-foreground italic">
                      By typing your name you confirm this questionnaire is complete and accurate to the best of your knowledge.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : null}
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <Button variant="outline" onClick={() => onClose(false)}>Close</Button>
          {formDef && !formDef.is_completed && (
            <Button onClick={handleSubmit} disabled={saving || loading} className="bg-primary hover:bg-primary/90 text-white">
              {saving
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                : <><CheckCircle2 className="w-4 h-4 mr-2" /> Complete & Sign</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuestionnaireCompletionDialog;
